import type { RootState } from "../store";
import type { ModalStackItem, ModalInstanceState } from "./types";

/**
 * Get the entire modal stack
 */
export const selectModalStack = (state: RootState): ModalStackItem[] =>
  state.modal.stack;

/**
 * Get stack size
 */
export const selectStackSize = (state: RootState): number =>
  state.modal.stack.length;

/**
 * Check if stack is empty
 */
export const selectIsStackEmpty = (state: RootState): boolean =>
  state.modal.stack.length === 0;

/**
 * Check if a modal exists in the stack by ID
 */
export const selectHasModal = (state: RootState, modalId: string): boolean =>
  state.modal.stack.some((m) => m.id === modalId);

/**
 * Get a specific modal by ID
 */
export const selectModalById = (
  state: RootState,
  modalId: string
): ModalStackItem | null =>
  state.modal.stack.find((m) => m.id === modalId) || null;

/**
 * Get the topmost modal
 */
export const selectTopModal = (state: RootState): ModalStackItem | null => {
  const stack = state.modal.stack;
  return stack.length > 0 ? stack[stack.length - 1] : null;
};

/**
 * Get all modal IDs in stack order
 */
export const selectModalIds = (state: RootState): string[] =>
  state.modal.stack.map((m) => m.id);

/**
 * Get modal instance state (loading, error) by ID
 */
export const selectModalState = (
  state: RootState,
  modalId: string
): ModalInstanceState | null => state.modal.modalStates[modalId] || null;

/**
 * Check if a specific modal is loading
 */
export const selectIsModalLoading = (
  state: RootState,
  modalId: string
): boolean => state.modal.modalStates[modalId]?.loading || false;

/**
 * Check if any modal is loading
 */
export const selectIsAnyModalLoading = (state: RootState): boolean =>
  Object.values(state.modal.modalStates).some((state) => state.loading);

/**
 * Get modal error by ID
 */
export const selectModalError = (
  state: RootState,
  modalId: string
): Error | null => state.modal.modalStates[modalId]?.error || null;

/**
 * Get any modal error (first found)
 */
export const selectAnyModalError = (state: RootState): Error | null => {
  for (const modalState of Object.values(state.modal.modalStates)) {
    if (modalState.error) return modalState.error;
  }
  return null;
};

/**
 * Get next z-index value
 */
export const selectNextZIndex = (state: RootState): number =>
  state.modal.nextZIndex;

/**
 * Get stack statistics for debugging
 */
export const selectModalStackStats = (state: RootState) => ({
  size: state.modal.stack.length,
  maxSize: 5, // MAX_STACK_SIZE constant
  nextZIndex: state.modal.nextZIndex,
  topModalId: state.modal.stack[state.modal.stack.length - 1]?.id || null,
  modalTypes: state.modal.stack.map((m) => m.type),
  modalStatesCount: Object.keys(state.modal.modalStates).length,
});
